import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { formatEventDate, formatPrice, formatAmount } from "@/lib/format";
import { Calendar, MapPin, Users, Edit, Trash2, UserPlus, Sparkles, Tag, Plus, Ticket as TicketIcon, BellRing, Send, Map as MapIcon, Gift } from "lucide-react";
import ZButton from "@/components/ZButton";
import { ZInput, ZSelect } from "@/components/ZInput";
import Modal from "@/components/Modal";
import { toast } from "sonner";

function CategoriesSection({ eventId, event, onChange }) {
  const cats = event.categories || [];
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", price: 0, capacity: 50, description: "", sale_ends_at: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const currency = event.currency || "GNF";

  const open = (cat) => {
    setErr("");
    if (cat) {
      setEditing(cat);
      setForm({
        name: cat.name, price: cat.price, capacity: cat.capacity,
        description: cat.description || "",
        sale_ends_at: cat.sale_ends_at ? new Date(cat.sale_ends_at).toISOString().slice(0, 16) : "",
      });
    } else {
      setEditing(null);
      setForm({ name: "", price: 0, capacity: 50, description: "", sale_ends_at: "" });
    }
    setShowAdd(true);
  };

  const save = async () => {
    setErr("");
    if (!form.name.trim()) return setErr("Nom obligatoire");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price) || 0,
        capacity: parseInt(form.capacity) || 0,
      };
      if (form.sale_ends_at) payload.sale_ends_at = new Date(form.sale_ends_at).toISOString();
      if (editing) {
        const r = await api.put(`/events/${eventId}/categories/${editing.id}`, payload);
        onChange(r.data);
        toast.success("Catégorie modifiée");
      } else {
        const r = await api.post(`/events/${eventId}/categories`, payload);
        onChange(r.data);
        toast.success("Catégorie ajoutée");
      }
      setShowAdd(false);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) return;
    try {
      const r = await api.delete(`/events/${eventId}/categories/${cat.id}`);
      onChange(r.data);
      toast.success("Catégorie supprimée");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const realCats = cats.filter((c) => c.id !== "legacy");

  return (
    <div style={{ marginBottom: 22 }} data-testid="categories-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}><TicketIcon size={16} style={{ verticalAlign: "middle" }} /> Catégories de billets</h3>
        <ZButton variant="outline" size="sm" onClick={() => open(null)} data-testid="add-cat-btn"><Plus size={14} /> Ajouter</ZButton>
      </div>
      {realCats.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--z-muted)" }}>Aucune catégorie — un prix unique de {formatPrice(event.price, currency)} s&apos;applique. Ajoutez une catégorie pour proposer plusieurs tarifs (VIP, Early Bird…).</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10 }}>
          {realCats.map((c) => (
            <div key={c.id} className="z-card" style={{ padding: 14 }} data-testid={`org-cat-${c.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{c.name}</div>
                  <div style={{ fontSize: 13, color: "var(--z-teal)", fontWeight: 600 }}>{formatPrice(c.price, currency)}</div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => open(c)} className="z-btn z-btn-ghost z-btn-sm" data-testid={`edit-cat-${c.id}`}><Edit size={14} /></button>
                  <button onClick={() => remove(c)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error,#c00)" }} data-testid={`del-cat-${c.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 6 }}>
                {c.sold}/{c.capacity} vendus
                {c.sale_ends_at && <> · Fin : {new Date(c.sale_ends_at).toLocaleDateString("fr-FR")}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title={editing ? "Modifier la catégorie" : "Nouvelle catégorie"} testId="cat-modal">
        <ZInput label="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="VIP, Early Bird…" data-testid="cat-name-input" />
        <ZInput label="Description (optionnel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-desc-input" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <ZInput label="Prix" type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="cat-price-input" />
          <ZInput label="Capacité" type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="cat-cap-input" />
        </div>
        <ZInput label="Fin de vente (Early Bird, optionnel)" type="datetime-local" value={form.sale_ends_at} onChange={(e) => setForm({ ...form, sale_ends_at: e.target.value })} data-testid="cat-end-input" />
        {err && <p className="z-error-text">{err}</p>}
        <div style={{ display: "flex", gap: 10 }}>
          <ZButton variant="outline" block onClick={() => setShowAdd(false)}>Annuler</ZButton>
          <ZButton block onClick={save} loading={busy} data-testid="cat-save-btn">Enregistrer</ZButton>
        </div>
      </Modal>
    </div>
  );
}

function PromoCodesSection({ eventId, event }) {
  const [promos, setPromos] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, max_uses: "", expires_at: "", category_ids: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const realCats = (event.categories || []).filter((c) => c.id !== "legacy");

  const load = () => {
    api.get(`/organizer/events/${eventId}/promos`).then((r) => setPromos(r.data)).catch(() => setPromos([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const create = async () => {
    setErr("");
    if (!form.code.trim()) return setErr("Code obligatoire");
    const dv = parseFloat(form.discount_value);
    if (!(dv > 0)) return setErr("Valeur de remise invalide");
    if (form.discount_type === "percent" && dv > 100) return setErr("Pourcentage > 100");
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: dv,
      };
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.category_ids.length > 0) payload.category_ids = form.category_ids;
      await api.post(`/events/${eventId}/promos`, payload);
      toast.success("Code promo créé");
      setShowAdd(false);
      setForm({ code: "", discount_type: "percent", discount_value: 10, max_uses: "", expires_at: "", category_ids: [] });
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    try {
      await api.delete(`/events/${eventId}/promos/${p.id}`);
      toast.success("Code supprimé");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleCat = (cid) => {
    setForm((f) => ({
      ...f,
      category_ids: f.category_ids.includes(cid)
        ? f.category_ids.filter((x) => x !== cid)
        : [...f.category_ids, cid],
    }));
  };

  return (
    <div style={{ marginBottom: 22 }} data-testid="promos-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}><Tag size={16} style={{ verticalAlign: "middle" }} /> Codes promo</h3>
        <ZButton variant="outline" size="sm" onClick={() => setShowAdd(true)} data-testid="add-promo-btn"><Plus size={14} /> Créer</ZButton>
      </div>
      {promos.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--z-muted)" }}>Aucun code promo. Créez-en pour offrir des remises ciblées (ex : FAMILY20 = -20%).</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10 }}>
          {promos.map((p) => (
            <div key={p.id} className="z-card" style={{ padding: 14 }} data-testid={`promo-${p.code}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontFamily: "monospace" }}>{p.code}</div>
                  <div style={{ fontSize: 13, color: "var(--z-orange)", fontWeight: 600 }}>
                    -{p.discount_type === "percent" ? `${p.discount_value}%` : formatAmount(p.discount_value, event.currency)}
                  </div>
                </div>
                <button onClick={() => remove(p)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error,#c00)" }} data-testid={`del-promo-${p.code}`}><Trash2 size={14} /></button>
              </div>
              <div style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 6 }}>
                Utilisé : {p.used_count}{p.max_uses ? ` / ${p.max_uses}` : " (illimité)"}
                {p.expires_at && <> · Expire : {new Date(p.expires_at).toLocaleDateString("fr-FR")}</>}
                {p.category_ids?.length > 0 && <> · {p.category_ids.length} catégorie(s) ciblée(s)</>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Nouveau code promo" testId="promo-modal">
        <ZInput label="Code (ex: FAMILY20)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} style={{ textTransform: "uppercase" }} data-testid="promo-code-form-input" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <ZSelect label="Type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="promo-type-input">
            <option value="percent">Pourcentage (%)</option>
            <option value="fixed">Montant fixe</option>
          </ZSelect>
          <ZInput label="Valeur" type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-value-input" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <ZInput label="Max utilisations (optionnel)" type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="promo-max-input" />
          <ZInput label="Expiration (optionnel)" type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-exp-input" />
        </div>
        {realCats.length > 0 && (
          <>
            <label className="z-label">Limiter à certaines catégories (sinon toutes)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {realCats.map((c) => (
                <button type="button" key={c.id} onClick={() => toggleCat(c.id)} className={`z-chip ${form.category_ids.includes(c.id) ? "z-chip-active" : ""}`} data-testid={`promo-cat-${c.id}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </>
        )}
        {err && <p className="z-error-text">{err}</p>}
        <div style={{ display: "flex", gap: 10 }}>
          <ZButton variant="outline" block onClick={() => setShowAdd(false)}>Annuler</ZButton>
          <ZButton block onClick={create} loading={busy} data-testid="promo-create-btn">Créer</ZButton>
        </div>
      </Modal>
    </div>
  );
}

function WaitlistSection({ eventId, event }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const realCats = (event.categories || []).filter((c) => c.id !== "legacy");

  const load = async () => {
    try {
      const r = await api.get(`/organizer/events/${eventId}/waitlist`);
      setItems(r.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/organizer/events/${eventId}/waitlist`);
        if (!cancelled) setItems(r.data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const filtered = filterCat ? items.filter((i) => i.category_id === filterCat) : items;
  const pending = filtered.filter((i) => !i.notified_at);

  const notify = async () => {
    if (pending.length === 0) return;
    if (!window.confirm(`Envoyer un e-mail à ${pending.length} personne(s) en attente ?`)) return;
    setBusy(true);
    try {
      const payload = filterCat ? { category_id: filterCat } : {};
      const r = await api.post(`/organizer/events/${eventId}/waitlist/notify`, payload);
      toast.success(`${r.data.notified} e-mail(s) envoyé(s)`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: 22 }} data-testid="waitlist-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
          <BellRing size={16} style={{ verticalAlign: "middle" }} /> Liste d&apos;attente ({items.length})
        </h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {realCats.length > 0 && (
            <select className="z-select" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ margin: 0, fontSize: 13, padding: "6px 10px" }} data-testid="waitlist-filter">
              <option value="">Toutes catégories</option>
              {realCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <ZButton size="sm" onClick={notify} loading={busy} disabled={pending.length === 0} data-testid="waitlist-notify-btn">
            <Send size={14} /> Notifier ({pending.length})
          </ZButton>
        </div>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}><span className="z-loader z-loader-dark" /></div>
      ) : filtered.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--z-muted)" }}>Aucune personne en attente. Vos billets ne sont pas encore complets.</p>
      ) : (
        <div className="z-card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "var(--z-bg)" }}>
              <tr>
                <th style={{ padding: 10, textAlign: "left", fontWeight: 600 }}>Nom</th>
                <th style={{ padding: 10, textAlign: "left", fontWeight: 600 }}>E-mail</th>
                <th style={{ padding: 10, textAlign: "left", fontWeight: 600 }}>Catégorie</th>
                <th style={{ padding: 10, textAlign: "left", fontWeight: 600 }}>Inscrit le</th>
                <th style={{ padding: 10, textAlign: "left", fontWeight: 600 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => {
                const cat = realCats.find((c) => c.id === w.category_id);
                return (
                  <tr key={w.id} style={{ borderTop: "1px solid var(--z-border, #eee)" }} data-testid={`waitlist-item-${w.id}`}>
                    <td style={{ padding: 10 }}>{w.name}</td>
                    <td style={{ padding: 10, fontFamily: "monospace", fontSize: 12 }}>{w.email}</td>
                    <td style={{ padding: 10 }}>{cat?.name || "—"}</td>
                    <td style={{ padding: 10, color: "var(--z-muted)" }}>{new Date(w.created_at).toLocaleDateString("fr-FR")}</td>
                    <td style={{ padding: 10 }}>
                      {w.notified_at
                        ? <span className="z-chip" style={{ fontSize: 11, background: "#e6f7f8", color: "var(--z-teal)" }}>Notifié</span>
                        : <span className="z-chip" style={{ fontSize: 11, background: "rgba(218,113,1,0.1)", color: "var(--z-orange)" }}>En attente</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function OrgEventDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDel, setShowDel] = useState(false);
  const [payingForfait, setPayingForfait] = useState(false);

  const load = () => {
    return api.get(`/organizer/events/${id}/stats`).then((r) => {
      setStats(r.data);
      setEvent(r.data.event);
    });
  };
  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  const payForfait = async () => {
    setPayingForfait(true);
    try {
      await api.post(`/events/${id}/pay-forfait`);
      toast.success("Forfait payé — événement soumis à la plateforme.");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPayingForfait(false);
    }
  };

  const remove = async () => {
    await api.delete(`/events/${id}`);
    nav("/org/events");
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!stats || !event) return <div className="z-container">Événement introuvable.</div>;

  const currency = event.currency || "GNF";

  return (
    <div data-testid="org-event-detail-page">
      {event.image_url && (
        <div style={{ width: "100%", aspectRatio: "16/9", maxHeight: 320, overflow: "hidden" }}>
          <img src={event.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <div className="z-container">
        <h1 className="z-page-title">{event.title}</h1>
        <p style={{ color: "var(--z-muted)", margin: "0 0 8px" }}>
          {event.is_private && <span className="z-badge" style={{ marginRight: 6 }}>Privé</span>}
          {event.category}
        </p>

        {event.status === "pending_payment" && (
          <div style={{ background: "rgba(218,113,1,0.10)", color: "var(--z-orange)", padding: 14, borderRadius: 12, marginBottom: 18, fontSize: 14 }} data-testid="org-event-forfait-banner">
            <div style={{ marginBottom: 10 }}>
              💳 <strong>Forfait à régler.</strong> Cet événement gratuit sera soumis à la plateforme une fois le forfait de{" "}
              <strong>{formatAmount(event.forfait_amount, event.forfait_currency || currency)}</strong> payé.
            </div>
            <ZButton size="sm" loading={payingForfait} onClick={payForfait} data-testid="pay-forfait-detail-btn">
              Payer le forfait (simulé)
            </ZButton>
          </div>
        )}
        {event.status === "pending" && (
          <div style={{ background: "rgba(218,113,1,0.10)", color: "var(--z-orange)", padding: 14, borderRadius: 12, marginBottom: 18, fontSize: 14 }} data-testid="org-event-pending-banner">
            ⏳ <strong>En attente de validation par la plateforme.</strong> Votre événement n&apos;est pas encore visible publiquement.
          </div>
        )}
        {event.status === "rejected" && (
          <div style={{ background: "rgba(161,44,123,0.10)", color: "var(--z-error)", padding: 14, borderRadius: 12, marginBottom: 18, fontSize: 14 }} data-testid="org-event-rejected-banner">
            ❌ <strong>Rejeté par l&apos;admin.</strong> {event.reject_reason && <>Motif : « {event.reject_reason} ». </>}Modifiez et republiez pour soumettre à nouveau.
          </div>
        )}

        <div className="z-card" style={{ padding: 16, marginBottom: 18, display: "grid", gap: 6 }}>
          <div style={{ display: "flex", gap: 8 }}><Calendar size={16} /> {formatEventDate(event.starts_at)}</div>
          <div style={{ display: "flex", gap: 8 }}><MapPin size={16} /> {event.location}</div>
          <div style={{ display: "flex", gap: 8 }}><Users size={16} /> {event.tickets_sold} / {event.capacity}</div>
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Statistiques</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 22 }}>
          <div className="z-kpi"><div className="z-kpi-label">Billets vendus</div><div className="z-kpi-value">{stats.tickets_total}</div></div>
          <div className="z-kpi"><div className="z-kpi-label">Scannés</div><div className="z-kpi-value">{stats.tickets_scanned}</div></div>
          <div className="z-kpi"><div className="z-kpi-label">Invitations</div><div className="z-kpi-value">{stats.tickets_invitations}</div></div>
          <div className="z-kpi"><div className="z-kpi-label">Revenus</div><div className="z-kpi-value">{formatAmount(stats.revenue, currency)}</div></div>
        </div>

        <CategoriesSection eventId={id} event={event} onChange={setEvent} />
        <PromoCodesSection eventId={id} event={event} />
        <WaitlistSection eventId={id} event={event} />

        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Actions</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
          <Link to={`/org/event/${event.id}/seat-map`} className="z-btn z-btn-teal" data-testid="manage-seatmap-btn"><MapIcon size={16} /> Plan de salle</Link>
          <Link to={`/org/event/${event.id}/table-plan`} className="z-btn z-btn-teal" data-testid="manage-tableplan-btn"><MapIcon size={16} /> Plan de table</Link>
          <Link to={`/org/event/${event.id}/gift-list`} className="z-btn z-btn-teal" data-testid="manage-giftlist-btn"><Gift size={16} /> Liste de cadeaux</Link>
          <Link to={`/org/agents/${event.id}`} className="z-btn z-btn-teal" data-testid="manage-agents-btn"><UserPlus size={16} /> Gérer les agents</Link>
          <Link to={`/org/engagement?event_id=${event.id}`} className="z-btn z-btn-teal" data-testid="create-engagement-btn"><Sparkles size={16} /> Lancer engagement</Link>
          <Link to={`/org/edit/${event.id}`} className="z-btn z-btn-outline" data-testid="edit-event-btn"><Edit size={16} /> Modifier</Link>
          <ZButton variant="danger" onClick={() => setShowDel(true)} data-testid="delete-event-btn"><Trash2 size={16} /> Supprimer</ZButton>
        </div>
      </div>

      <Modal open={showDel} onClose={() => setShowDel(false)} title="Supprimer cet événement ?" testId="delete-modal">
        <p style={{ color: "var(--z-muted)", marginBottom: 18 }}>
          Cette action est irréversible. Tous les billets associés seront conservés mais ne pourront plus être validés.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <ZButton variant="outline" block onClick={() => setShowDel(false)} data-testid="delete-cancel-btn">Annuler</ZButton>
          <ZButton variant="danger" block onClick={remove} data-testid="delete-confirm-btn">Supprimer</ZButton>
        </div>
      </Modal>
    </div>
  );
}
